import * as THREE from "three";

export type SiteWorldPoseContext = {
  sceneObjects: Record<string, THREE.Object3D> | null | undefined;
  bodies: Record<string, unknown> | null | undefined;
  sites: Record<string, unknown> | null | undefined;
  siteUuid: string | null | undefined;
  modelGroup: THREE.Object3D | null | undefined;
};

export function getBodyWorldPoseFromRegistry(
  sceneObjects: Record<string, THREE.Object3D> | null | undefined,
  bodyUuid: string | null | undefined
): { position: THREE.Vector3; quaternion: THREE.Quaternion } | null {
  const obj = bodyUuid ? sceneObjects?.[bodyUuid] : null;
  if (!obj) return null;
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  obj.getWorldPosition(position);
  obj.getWorldQuaternion(quaternion);
  return { position, quaternion };
}

export function getSiteWorldPoseForSavedCamera({
  sceneObjects,
  bodies,
  sites,
  siteUuid,
  modelGroup,
}: SiteWorldPoseContext): { position: THREE.Vector3; quaternion: THREE.Quaternion } | null {
  if (!siteUuid) return null;
  const bodiesMap = bodies || {};
  const sitesMap = sites || {};
  const site = sitesMap[siteUuid] as { position?: unknown; orientation?: unknown } | undefined;
  if (!site) return null;

  const parentBodyUuid =
    (
      Object.values(bodiesMap).find(
        (body) =>
          body &&
          typeof body === "object" &&
          Array.isArray((body as { sites?: unknown[] }).sites) &&
          (body as { sites: unknown[] }).sites.includes(siteUuid)
      ) as { uuid?: string } | undefined
    )?.uuid ?? null;

  let bodyPose: { position: THREE.Vector3; quaternion: THREE.Quaternion };

  const parentPose = parentBodyUuid
    ? getBodyWorldPoseFromRegistry(sceneObjects, parentBodyUuid)
    : null;
  if (parentBodyUuid && parentPose) {
    bodyPose = parentPose;
  } else if (modelGroup && typeof modelGroup.updateMatrixWorld === "function") {
    modelGroup.updateMatrixWorld(true);
    const wp = new THREE.Vector3();
    const wq = new THREE.Quaternion();
    const ws = new THREE.Vector3();
    modelGroup.matrixWorld.decompose(wp, wq, ws);
    bodyPose = { position: wp, quaternion: wq };
  } else {
    bodyPose = {
      position: new THREE.Vector3(0, 0, 0),
      quaternion: new THREE.Quaternion(0, 0, 0, 1),
    };
  }

  const posArr = Array.isArray(site.position) ? site.position : [0, 0, 0];
  const position = new THREE.Vector3(
    Number(posArr[0]) || 0,
    Number(posArr[1]) || 0,
    Number(posArr[2]) || 0
  );
  const orientation =
    Array.isArray(site.orientation) && site.orientation.length === 4
      ? site.orientation
      : [1, 0, 0, 0];
  const quaternion = newTHREEQuaternionFromWxyz(orientation);

  position.applyQuaternion(bodyPose.quaternion).add(bodyPose.position);
  quaternion.premultiply(bodyPose.quaternion);
  return { position, quaternion };
}

function newTHREEQuaternionFromWxyz(quat: number[] | unknown[]): THREE.Quaternion {
  return new THREE.Quaternion(
    Number(quat[1]) || 0,
    Number(quat[2]) || 0,
    Number(quat[3]) || 0,
    Number(quat[0]) || 1
  );
}

/** World-space translation gizmo axes: fixed = world XYZ; mounted = mount parent local axes in world space. */
export function getSavedCameraTranslationGizmoQuaternion(
  cameraDef: Record<string, unknown> | null | undefined,
  sceneObjects: Record<string, THREE.Object3D> | null | undefined,
  bodies: Record<string, unknown> | null | undefined,
  sites: Record<string, unknown> | null | undefined,
  modelGroup: THREE.Object3D | null
): THREE.Quaternion {
  if (!cameraDef || cameraDef.kind !== "mounted") {
    return new THREE.Quaternion().identity();
  }
  const mountType = cameraDef.mountType === "site" ? "site" : "body";
  if (mountType === "site") {
    const pose = getSiteWorldPoseForSavedCamera({
      sceneObjects,
      bodies,
      sites,
      siteUuid: typeof cameraDef.mountSiteUuid === "string" ? cameraDef.mountSiteUuid : null,
      modelGroup,
    });
    return pose ? pose.quaternion.clone() : new THREE.Quaternion().identity();
  }
  const bp = getBodyWorldPoseFromRegistry(
    sceneObjects,
    typeof cameraDef.mountBodyUuid === "string" ? cameraDef.mountBodyUuid : null
  );
  return bp ? bp.quaternion.clone() : new THREE.Quaternion().identity();
}

export function getSavedCameraWorldPose(
  cameraDef: Record<string, unknown> | null | undefined,
  sceneObjects: Record<string, THREE.Object3D> | null | undefined,
  bodies: Record<string, unknown> | null | undefined,
  sites: Record<string, unknown> | null | undefined,
  modelGroup: THREE.Object3D | null
): { position: THREE.Vector3; quaternion: THREE.Quaternion } {
  const pos = Array.isArray(cameraDef?.position) ? (cameraDef?.position as number[]) : [0, 0.5, 2];
  const quat =
    Array.isArray(cameraDef?.orientation) && (cameraDef?.orientation as unknown[]).length === 4
      ? (cameraDef?.orientation as number[])
      : [1, 0, 0, 0];
  const localPosition = new THREE.Vector3(
    Number(pos[0]) || 0,
    Number(pos[1]) || 0,
    Number(pos[2]) || 0
  );
  const localQuaternion = newTHREEQuaternionFromWxyz(quat).normalize();

  if (cameraDef?.kind !== "mounted") {
    return { position: localPosition, quaternion: localQuaternion };
  }

  const mountType = cameraDef.mountType === "site" ? "site" : "body";
  const sourcePose =
    mountType === "site"
      ? getSiteWorldPoseForSavedCamera({
          sceneObjects,
          bodies,
          sites,
          siteUuid: typeof cameraDef.mountSiteUuid === "string" ? cameraDef.mountSiteUuid : null,
          modelGroup,
        })
      : getBodyWorldPoseFromRegistry(
          sceneObjects,
          typeof cameraDef.mountBodyUuid === "string" ? cameraDef.mountBodyUuid : null
        );

  if (!sourcePose) {
    return { position: localPosition, quaternion: localQuaternion };
  }

  return {
    position: localPosition.clone().applyQuaternion(sourcePose.quaternion).add(sourcePose.position),
    quaternion: sourcePose.quaternion.clone().multiply(localQuaternion),
  };
}
